import { NotificationEnum } from "@graphql/generated/graphql.g";
import {
  NotificationFragment,
  useNotificationsQuery,
} from "@graphql/notifications/Notifications.g";
import {
  type ColumnDef,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { useTableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/useTableColumnSettingsProvider";

import { Box } from "@design-system/Box/Box";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Table } from "@design-system/Table/Table";

import { routes } from "@pages/routes";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";
import { ConfigurableTableSystem } from "@utils/table/TableConfigurationSystem";

import { useNotification } from "@providers/NotificationProvider/useNotification";

import {
  NotificationsHistoryTableConfig,
  NotificationsTableConfig,
  renderApplicationName,
  renderClientName,
  renderCreatedAt,
  renderNotificationType,
  renderResolvedAt,
  renderResolvedBy,
} from "./NotificationsTableConfig";
import { NotificationTableColumns } from "./UI/NotificationsTableColumns";

const PAGE_SIZE = 30;

const columnHelper = createColumnHelper<NotificationFragment>();

export type NotificationsViewMode = "active" | "history";

type Props = {
  initialPageIndex?: number;
  viewMode?: NotificationsViewMode;
  clientId?: string;
  notificationType?: NotificationEnum;
  applicationId?: string;
  applicationNameSearch?: string;
  resolvedById?: string;
  resolvedAt_Gte?: string;
  resolvedAt_Lte?: string;
};

export const NotificationsTable = ({
  initialPageIndex = 0,
  viewMode = "active",
  clientId,
  notificationType,
  applicationId,
  applicationNameSearch,
  resolvedById,
  resolvedAt_Gte,
  resolvedAt_Lte,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: PAGE_SIZE,
  });
  const [sorting, setSorting] = useState<SortingState>(() =>
    viewMode === "history"
      ? [{ id: NotificationTableColumns.resolvedAt, desc: true }]
      : [],
  );

  const { columnSettings, columnOrder } = useTableColumnSettingsProvider();
  const isHistoryView = viewMode === "history";
  const tableConfig = isHistoryView
    ? NotificationsHistoryTableConfig
    : NotificationsTableConfig;

  const effectiveColumnSettings = useMemo(() => {
    if (!isHistoryView) {
      return {
        ...columnSettings,
        [NotificationTableColumns.resolvedBy]: false,
        [NotificationTableColumns.resolvedAt]: false,
      };
    }

    return columnSettings;
  }, [columnSettings, isHistoryView]);

  const queryVariables = useMemo(() => {
    const variables: Parameters<typeof useNotificationsQuery>[0] = {
      first: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      resolvedAt_Isnull: !isHistoryView,
      sortBy: ConfigurableTableSystem.generateSortParameter(
        tableConfig.columns,
        sorting,
      ),
    };

    if (clientId) {
      variables.clientId = clientId;
    }

    if (notificationType) {
      variables.notificationType = notificationType;
    }

    if (applicationId) {
      variables.applicationId = applicationId;
    }

    if (isHistoryView) {
      if (resolvedById) {
        variables.resolvedById = resolvedById;
      }

      if (resolvedAt_Gte) {
        variables.resolvedAt_Gte = resolvedAt_Gte;
      }

      if (resolvedAt_Lte) {
        variables.resolvedAt_Lte = resolvedAt_Lte;
      }
    }

    if (applicationNameSearch && applicationNameSearch.trim()) {
      variables.application_Resource_ProductName_Icontains =
        applicationNameSearch.trim();
    }

    return variables;
  }, [
    pagination.pageSize,
    pagination.pageIndex,
    sorting,
    clientId,
    notificationType,
    applicationId,
    applicationNameSearch,
    isHistoryView,
    resolvedById,
    resolvedAt_Gte,
    resolvedAt_Lte,
    tableConfig.columns,
  ]);

  const notificationsQuery = useNotificationsQuery(queryVariables);

  const { resolveNotification } = useNotification();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const totalCount =
    notificationsQuery.data?.notifications?.totalCount ?? undefined;

  const data = useMemo(() => {
    return (
      notificationsQuery.data?.notifications?.edges
        .map((edge) => edge?.node)
        .filter((notification) => !!notification) ?? []
    );
  }, [notificationsQuery.data]);

  const handleDetailsClick = useCallback(
    (clientId: string, applicationId: string) => {
      navigate(
        routes.portal.clients.client.applications.application.$buildPath({
          params: { clientId, applicationId },
        }),
        {
          viewTransition: true,
        },
      );
    },
    [navigate],
  );

  const handleNotificationClick = useCallback(
    (notificationId: string) => {
      setSearchParams((prev) => {
        prev.set("notificationId", notificationId);
        return prev;
      });
    },
    [setSearchParams],
  );

  const handleResolveClick = useCallback(
    async (id: string) => {
      await resolveNotification(id);
    },
    [resolveNotification],
  );

  const configuredColumns = useMemo(() => {
    const configWithRenderers = {
      ...tableConfig,
      columns: Object.fromEntries(
        Object.entries(tableConfig.columns).map(([key, config]) => [
          key,
          {
            ...config,
            render: (value: unknown, row: NotificationFragment) => {
              const columnKey = key as NotificationTableColumns;

              switch (columnKey) {
                case NotificationTableColumns.client:
                  return (
                    <Clickable
                      onClick={() =>
                        handleDetailsClick(
                          row.client?.id || "",
                          row.application?.id || "",
                        )
                      }
                      outline="md"
                      stretch
                    >
                      {renderClientName(value)}
                    </Clickable>
                  );
                case NotificationTableColumns.applicationName:
                  return renderApplicationName(
                    value,
                    row,
                    handleDetailsClick,
                  );
                case NotificationTableColumns.notificationType:
                  return renderNotificationType(
                    value,
                    row,
                    handleNotificationClick,
                  );
                case NotificationTableColumns.createdAt:
                  return renderCreatedAt(value);
                case NotificationTableColumns.resolvedBy:
                  return renderResolvedBy(value, row);
                case NotificationTableColumns.resolvedAt:
                  return renderResolvedAt(value);
                default:
                  return String(value);
              }
            },
          },
        ]),
      ) as typeof tableConfig.columns,
    };

    return ConfigurableTableSystem.generateColumnsFromConfig(
      data,
      configWithRenderers,
      {
        columnOrder:
          columnOrder.length > 0
            ? (columnOrder as NotificationTableColumns[])
            : undefined,
        columnVisibility: effectiveColumnSettings,
      },
    );
  }, [
    data,
    columnOrder,
    effectiveColumnSettings,
    handleDetailsClick,
    handleNotificationClick,
    tableConfig,
  ]);

  const allColumnsWithActions = useMemo(() => {
    const columns: ColumnDef<NotificationFragment, unknown>[] = [
      ...configuredColumns,
    ];

    columns.push(
      columnHelper.display({
        id: "actions",
        header: "",
        meta: {
          sticky: true,
          actions: true,
        },
        cell: (props) => {
          return (
            <Box gap={2}>
              <Dropdown
                trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
              >
                <Dropdown.Option
                  label="Details"
                  icon="faFileLines"
                  onSelect={() => handleNotificationClick(props.row.original.id)}
                />
                {!isHistoryView && (
                  <Dropdown.Option
                    label="Resolve"
                    icon="faCheck"
                    onSelect={() => handleResolveClick(props.row.original.id)}
                  />
                )}
              </Dropdown>
            </Box>
          );
        },
      }),
    );

    return columns;
  }, [
    configuredColumns,
    handleNotificationClick,
    handleResolveClick,
    isHistoryView,
  ]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <Table
      columns={allColumnsWithActions as ColumnDef<NotificationFragment>[]}
      data={data}
      rowCount={totalCount}
      onPaginationChange={handlePaginationChange}
      pagination={pagination}
      isLoading={notificationsQuery.isLoading}
      isFetching={notificationsQuery.isFetching}
      onPageSizeChange={handlePageSizeChange}
      sorting={sorting}
      onSortingChange={setSorting}
      columnVisibility={effectiveColumnSettings}
    />
  );
};
